import classNames from "classnames";
import React, {
  AnimationEvent,
  ChangeEvent,
  DragEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useRef,
  useState
} from "react";
import KuikaCloseButton from "../kuika-close-button/kuika-close-button";
import { IAcceptPartition, partitionByAccept } from "./accept-match";
import "./kuika-file-input.scss";

// UIKit file-picker primitive. Wraps a hidden `<input type="file">` and
// surfaces a visible button (matching the Kuika button visual contract)
// plus an optional clear (×) affordance for previously-selected values.
// Radix has no file-input primitive — file pickers need direct browser
// access to open the OS dialog — so this is a native input under a thin
// UIKit shell rather than a Radix wrap.
//
// Three callback shapes:
//   onChange — receives the picked File OR null when cleared
//   onChangeAsBase64 — convenience for the common "store as base64
//     string" case. The promise resolves after FileReader finishes;
//     callers awaiting it can show a spinner if desired.
//   onFiles — every file of a pick or a drop, in one call. The only
//     callback the `dropzone` variant fires, and the only one that sees
//     more than one file when `multiple` is set.
//
// A drop on the `dropzone` variant is filtered by `accept` the way the OS
// dialog filters a pick: the three callbacks above see only the accepted
// files, `onReject` gets the rest, and the zone shakes and says why.

export interface IKuikaFileInputProps {
  // Value identifier shown on the trigger when present. Examples:
  // "image · 4.2 KB" / "2 files selected". The component never
  // interprets this — display only.
  valueLabel?: string;
  // OS file dialog filter — same shape as `<input accept>`. Defaults
  // open to "all files".
  accept?: string;
  disabled?: boolean;
  // True while the consumer is reading / processing the file. Disables
  // the trigger and swaps its label to a spinner.
  busy?: boolean;
  // Trigger size — matches KuikaButton's contract for visual parity.
  size?: "sm" | "md";
  // Label shown when no value is present. Default "Choose file…".
  emptyLabel?: string;
  // Label shown while the consumer's busy flag is true. Default "Reading…".
  busyLabel?: string;
  // Render the clear (×) affordance when valueLabel is non-empty.
  // Default true; callers that want a non-clearable picker can opt out.
  showClear?: boolean;
  // Layout variant — "cell" flattens the trigger to fit inside a grid
  // cell (no inset border, full-cell width); "standalone" gets a
  // standard outlined button; "dropzone" is a dashed drop target that
  // renders `children` and also opens the dialog on click or Enter/Space.
  // Default "standalone".
  variant?: "standalone" | "cell" | "dropzone";
  // Accept more than one file per pick or drop. Only `onFiles` reports
  // the extra ones — `onChange`/`onChangeAsBase64` stay single-file.
  multiple?: boolean;
  // Dropzone content. Ignored by the other variants.
  children?: ReactNode;
  className?: string;
  // Fires on every pick. Receives the File (or null when cleared).
  onChange?: (file: File | null) => void;
  // Convenience for the common base64 case. Skipped when null is
  // returned from clear; only fires on a successful pick.
  onChangeAsBase64?: (base64: string, file: File) => void;
  // Fires with every file of a pick or a drop.
  onFiles?: (files: File[]) => void;
  // Fires with the files `accept` turned away — a dialog pick (any variant, the OS "All files"
  // filter escape hatch) or a dropzone drop, filtered the same way.
  onReject?: (files: File[]) => void;
  // What the zone says for a moment after turning a file away. Default
  // "That file type isn't accepted here".
  rejectLabel?: string;
  onBlur?: () => void;
  // Optional title attribute on the trigger (hover tooltip).
  title?: string;
  dataTestId?: string;
  // `id` for the hidden native input. Tests that drive a picker do it by
  // setting files on that input, so they need a handle on it.
  inputId?: string;
}

// How long the zone keeps saying why it turned a file away before its own
// content comes back. A new drag or a click brings it back sooner.
const REJECT_NOTICE_MS: number = 3000;

const readAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject): void => {
    const reader: FileReader = new FileReader();
    reader.onload = (): void => {
      const result: string = typeof reader.result === "string" ? reader.result : "";
      // Strip the `data:<mime>;base64,` prefix — callers store the raw
      // base64 the backend expects, not the data-URI form.
      const commaIdx: number = result.indexOf(",");
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = (): void => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });
};

const KuikaFileInput: React.FC<IKuikaFileInputProps> = (props: IKuikaFileInputProps): JSX.Element => {
  const {
    valueLabel,
    accept,
    disabled,
    busy: externalBusy,
    size = "md",
    emptyLabel = "Choose file…",
    busyLabel = "Reading…",
    showClear = true,
    variant = "standalone",
    multiple = false,
    children,
    className,
    onChange,
    onChangeAsBase64,
    onFiles,
    onReject,
    rejectLabel = "That file type isn't accepted here",
    onBlur,
    title,
    dataTestId,
    inputId
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [internalBusy, setInternalBusy] = useState<boolean>(false);
  // Drag events fire on every child the pointer crosses, so a plain
  // dragleave handler would clear the highlight while the file is still
  // over the zone. Count enters against leaves and only clear at zero.
  const dragDepth = useRef<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  // A turned-away drop: the shake runs once (cleared on its own
  // animationend, the same way KuikaButton's denied shake is), the notice
  // stays for REJECT_NOTICE_MS after the latest rejection. The notice is a
  // counter, not a flag, so a second rejection inside the window restarts
  // the timer instead of inheriting the first one's deadline. 0 = no notice.
  const [isShaking, setIsShaking] = useState<boolean>(false);
  const [rejectNotice, setRejectNotice] = useState<number>(0);
  const isRejectNoticeShown: boolean = rejectNotice > 0;
  const busy: boolean = externalBusy === true || internalBusy;
  const hasValue: boolean = typeof valueLabel === "string" && valueLabel.length > 0;
  const isDisabled: boolean = disabled === true || busy;

  const emitFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0) {
      return;
    }
    onFiles?.(files);
    const first: File = files[0];
    onChange?.(first);
    if (onChangeAsBase64 !== undefined) {
      setInternalBusy(true);
      try {
        const b64: string = await readAsBase64(first);
        onChangeAsBase64(b64, first);
      } finally {
        setInternalBusy(false);
      }
    }
  };

  const handlePick = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files: File[] = Array.from(e.target.files ?? []);
    // Reset the native input so picking the same file twice still fires
    // a change event (browsers dedupe identical values).
    e.target.value = "";
    // The OS dialog's `accept` filter has an "All files" escape hatch (most visibly on
    // Windows), so a pick can carry a file the dialog itself was meant to keep out. Filtered
    // the same way a drop is, so a picked file can never slip past `accept` when a dropped one
    // could not.
    const { accepted, rejected }: IAcceptPartition = partitionByAccept(files, accept);
    if (rejected.length > 0) {
      onReject?.(rejected);
    }
    await emitFiles(accepted);
  };

  useEffect((): (() => void) | void => {
    if (rejectNotice === 0) {
      return;
    }
    const timer: number = window.setTimeout((): void => setRejectNotice(0), REJECT_NOTICE_MS);
    return (): void => window.clearTimeout(timer);
  }, [rejectNotice]);

  const openDialog = (): void => {
    if (isDisabled) {
      return;
    }
    setRejectNotice(0);
    inputRef.current?.click();
  };

  const wrapperClass: string = classNames(
    "kuika-file-input",
    `kuika-file-input--${variant}`,
    `kuika-file-input--${size}`,
    {
      "kuika-file-input--disabled": disabled,
      "kuika-file-input--busy": busy,
      "kuika-file-input--dragging": isDragging,
      "kuika-file-input--rejected": isRejectNoticeShown,
      "kuika-file-input--shaking": isShaking
    },
    className
  );

  const fileInput: JSX.Element = (
    <input ref={inputRef} id={inputId} type="file" hidden multiple={multiple} accept={accept} onChange={handlePick} />
  );

  if (variant === "dropzone") {
    const handleDragEnter = (e: DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      if (isDisabled) {
        return;
      }
      dragDepth.current += 1;
      setIsDragging(true);
      setRejectNotice(0);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) {
        setIsDragging(false);
      }
    };

    // Without a dragover handler that preventDefaults, the browser opens
    // the dropped file in the tab instead of giving it to the drop handler.
    const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      if (!isDisabled) {
        e.dataTransfer.dropEffect = "copy";
      }
    };

    const handleDrop = async (e: DragEvent<HTMLDivElement>): Promise<void> => {
      e.preventDefault();
      dragDepth.current = 0;
      setIsDragging(false);
      if (isDisabled) {
        return;
      }
      const dropped: File[] = Array.from(e.dataTransfer.files ?? []);
      const { accepted, rejected }: IAcceptPartition = partitionByAccept(dropped, accept);
      if (rejected.length > 0) {
        setIsShaking(true);
        setRejectNotice((previous: number): number => previous + 1);
        onReject?.(rejected);
      }
      await emitFiles(multiple ? accepted : accepted.slice(0, 1));
    };

    const handleAnimationEnd = (e: AnimationEvent<HTMLDivElement>): void => {
      // Animations inside the zone's content bubble here too; only the
      // zone's own shake ends it.
      if (e.target === e.currentTarget) {
        setIsShaking(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key !== "Enter" && e.key !== " ") {
        return;
      }
      e.preventDefault();
      openDialog();
    };

    return (
      <div
        className={wrapperClass}
        // A zone can hold its own button (an "Upload" call to action), so
        // it cannot be a <button> itself — that would nest interactive
        // elements. This is the shape antd's Dragger used for the same
        // reason.
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        title={title}
        data-testid={dataTestId}
        onClick={openDialog}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onAnimationEnd={handleAnimationEnd}
        onBlur={onBlur}
      >
        {/* The content and the notice share one grid cell, so the zone keeps
            its height while one fades into the other. The notice stays in
            the DOM (empty when idle) so its live region is announced. */}
        <div className="kuika-file-input__zone-stack">
          <div className="kuika-file-input__zone-content" aria-hidden={isRejectNoticeShown || undefined}>
            {children}
          </div>
          <div className="kuika-file-input__zone-notice" aria-live="polite">
            {isRejectNoticeShown ? rejectLabel : ""}
          </div>
        </div>
        {fileInput}
      </div>
    );
  }

  let triggerLabel: string | undefined = emptyLabel;
  if (busy) {
    triggerLabel = busyLabel;
  } else if (hasValue) {
    triggerLabel = valueLabel;
  }

  return (
    <span className={wrapperClass}>
      <button
        type="button"
        className="kuika-file-input__trigger"
        disabled={isDisabled}
        onClick={openDialog}
        onBlur={onBlur}
        title={title ?? (hasValue ? "Choose a different file" : "Click to upload a file")}
        data-testid={dataTestId}
      >
        {/* The label clips its own overflow so the trigger can carry a hit
            area larger than itself. */}
        <span className="kuika-file-input__label">{triggerLabel}</span>
      </button>
      {hasValue && showClear && !disabled && (
        <KuikaCloseButton
          size="sm"
          className="kuika-file-input__clear"
          aria-label="Clear"
          onClick={(): void => onChange?.(null)}
        />
      )}
      {fileInput}
    </span>
  );
};

export default KuikaFileInput;
